/**
 * The one place this app talks to the meeting-notes backend.
 *
 * Every `src/data/` module goes through `apiGet`/`apiSend` rather than calling
 * `fetch` itself, so the three things that are true of every request — the base
 * URL, the Clerk bearer token, and what a non-2xx means — are stated once.
 *
 * **The wire format is snake_case and the app is camelCase.** That boundary is
 * here and nowhere else: `src/data/` maps a response into the domain types the
 * surfaces render, so no component ever sees a `display_order`. Response types
 * in this file are therefore deliberately loose — they describe what the server
 * sends, not what the app uses.
 */

/**
 * Absolute, not same-origin.
 *
 * The old Next.js client used `NEXT_PUBLIC_API_URL` against a backend on its
 * own host, so the backend's CORS allowlist (`FRONTEND_URLS`) is already the
 * mechanism in production. Keeping that means the SPA can be served as static
 * files by anything, with no proxy tier to deploy or keep in step.
 */
const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

/**
 * Set by `<ApiTokenBridge>` once Clerk has loaded.
 *
 * A module-level getter rather than a React context because `src/data/` is
 * plain async functions called from query functions, not hooks — threading a
 * token through every signature would put auth in the type of every read.
 */
type TokenGetter = () => Promise<string | null>

let getToken: TokenGetter = async () => null

export function setTokenGetter(getter: TokenGetter): void {
  getToken = getter
}

/**
 * The signed-in Clerk user id.
 *
 * Needed as a *path segment* by the calendar routes (`GET /meetings/:userId`,
 * `/auth/preferences/:userId`), which predate the token carrying it. Everything
 * else identifies the caller from the bearer token alone.
 */
let userId: string | null = null

export function setCurrentUserId(id: string | null): void {
  userId = id
}

export function currentUserId(): string {
  if (!userId) throw new Error('No signed-in user yet')
  return userId
}

/**
 * Whether the signed-in account administers the organisation.
 *
 * Mirrored out of React because `src/data/` decides which endpoint to call
 * from it — an admin reads the organisation's upcoming meetings, everyone else
 * reads their own — and those are plain async functions, not hooks.
 */
let orgAdmin = false

export function setIsOrgAdmin(value: boolean): void {
  orgAdmin = value
}

export function isOrgAdmin(): boolean {
  return orgAdmin
}

/** A non-2xx response, carrying the status so callers can branch on 403 vs 500. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** The signed-in user may not do this. Surfaces render it as a state, not a toast. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

/**
 * The server's error shape, best-effort.
 *
 * Routes are not consistent about it — some send `{ error }`, some `{ message }`,
 * some a bare string — so the message is whatever can be found, and the status
 * is what callers should actually branch on.
 */
async function errorMessage(response: Response): Promise<string> {
  const text = await response.text().catch(() => '')
  if (!text) return response.statusText || `HTTP ${response.status}`
  try {
    const body = JSON.parse(text) as { error?: string; message?: string; detail?: string }
    return body.error ?? body.message ?? body.detail ?? text.slice(0, 200)
  } catch {
    return text.slice(0, 200)
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** GET, parsed as JSON. */
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: { ...(await authHeaders()) },
    signal,
  })
  if (!response.ok) throw new ApiError(response.status, path, await errorMessage(response))
  return (await response.json()) as T
}

/**
 * A write. `body` is serialised as JSON unless it is already `FormData`, which
 * is what the logo endpoints take — letting the browser set its own multipart
 * boundary is why `Content-Type` is conditional rather than constant.
 */
export async function apiSend<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const isForm = body instanceof FormData
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      ...(await authHeaders()),
      ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  })
  if (!response.ok) throw new ApiError(response.status, path, await errorMessage(response))
  // 204 and empty 200s are ordinary for DELETE; `json()` would throw on them.
  const text = await response.text()
  return (text ? JSON.parse(text) : {}) as T
}

/**
 * An absolute URL for a backend path, for the cases a bare `fetch` cannot serve:
 * a download the browser performs itself, or an `<img src>`.
 */
export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`
}

/**
 * A blob download that still carries the bearer token.
 *
 * `<a download>` cannot send an Authorization header, so the file is fetched
 * here and handed to the browser as an object URL.
 */
export async function apiDownload(path: string): Promise<Blob> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: { ...(await authHeaders()) },
  })
  if (!response.ok) throw new ApiError(response.status, path, await errorMessage(response))
  return response.blob()
}

/**
 * A streaming POST, for the assistant's server-sent events.
 *
 * Returns the raw byte stream rather than parsed events: decoding SSE needs to
 * know what an event *means*, and that belongs with the caller who defined the
 * event type, not in the transport.
 */
export async function apiStream(
  path: string,
  body: unknown,
  signal: AbortSignal
): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      ...(await authHeaders()),
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal,
  })
  if (!response.ok) throw new ApiError(response.status, path, await errorMessage(response))
  if (!response.body) throw new ApiError(502, path, 'The assistant returned no stream.')
  return response.body
}
